import {
  BonusBandwidthStatus,
  Partner,
  Product,
  ProductsWithEligibility,
  PurchaseEligibilityStatus,
  ShopStatus,
} from 'src/api/@generated/backend';

import { RootState } from 'src/state/types';
import { getOrThrow } from 'src/utils/type-utils';

import { Resource, Request, RequestStatus, RequestType, RequestState } from './types';

export const getRequest =
  (requestType: RequestType) =>
  (state: RootState): Request<Resource> =>
    state.request[requestType];

export const selectRequestsState = (state: RootState): RequestState => state.request;

export const selectRequestDataOrThrow =
  <T extends Resource>(requestType: RequestType) =>
  (state: RootState): T =>
    getOrThrow(
      selectRequestData(requestType)(state) as T,
      `Trying to access request "${requestType}" resource data when none is present`,
    );

export const selectRequestData =
  <T extends Resource>(requestType: RequestType) =>
  (state: RootState): T => {
    const data = getRequest(requestType)(state).data;

    return data as T;
  };

export const areRequestsReady = (requestsState: RequestState, ...requestTypeList: RequestType[]): boolean =>
  requestTypeList.every(
    (requestType) =>
      requestsState[requestType].status === RequestStatus.SUCCESS ||
      (requestType === RequestType.GET_PARTNERS && requestsState[requestType].status === RequestStatus.FAILURE),
  );

export const anyStartedOrFailedRequests = (state: RootState): boolean =>
  Object.keys(state.request).some((requestType) =>
    [RequestStatus.FAILURE, RequestStatus.IN_PROGRESS].includes(state.request[requestType as RequestType].status),
  );

export const selectPartnerListIfDefined = (state: RootState): Partner[] | undefined =>
  selectRequestData<Partner[]>(RequestType.GET_PARTNERS)(state);

export const selectPurchaseEligibilityStatus = (state: RootState): PurchaseEligibilityStatus =>
  selectProductsWithEligibility(state).purchaseEligibilityStatus;

export const selectProductsWithEligibility = (state: RootState): ProductsWithEligibility =>
  selectRequestDataOrThrow<ProductsWithEligibility>(RequestType.GET_PRODUCTS_WITH_ELIGIBILITY)(state);

export const selectBonusBandwidthStatusOrThrow = (state: RootState): BonusBandwidthStatus =>
  getOrThrow(
    selectProductsWithEligibility(state).bonusBandwidthStatus,
    'Invalid state. Bonus bandwidth status must be present',
  );

export const selectIsShopEnabled = (state: RootState): boolean =>
  selectRequestDataOrThrow<ShopStatus>(RequestType.GET_SHOP_STATUS)(state).enabled;

export const selectProductsOrThrow = (state: RootState): Product[] =>
  getOrThrow(selectProductsWithEligibility(state).products, 'Invalid state. Products must be present');
